/**
 * Supplier API client (Phase 3 — httpOnly cookies).
 * withCredentials; no localStorage JWTs; single-flight cookie refresh.
 */
import axios from 'axios';
import { config } from '@/config';
import { ensureApiV1Base } from '@/config/apiBase';
import { normalizeApiError } from '@/utils/errorHandler';
import { shouldAttemptCookieRefresh } from '@/utils/shouldAttemptCookieRefresh';
import { attachSupplierAxiosPerf } from '@/platforms/supplier/perf/supplierPerfDiagnostics';
import { notifySupplierSessionExpired } from '@/platforms/supplier/utils/supplierSessionEvents';

const API_BASE = ensureApiV1Base(config.SUPPLIER_API_BASE_URL || config.API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let refreshPromise = null;

function stripLegacyJwtKeys() {
  try {
    localStorage.removeItem('supplier_access_token');
    localStorage.removeItem('supplier_refresh_token');
  } catch (_) {}
}

function runRefresh() {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/supplier/auth/refresh', {})
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use(
  (cfg) => {
    cfg.withCredentials = true;
    if (cfg.headers) {
      delete cfg.headers.Authorization;
      delete cfg.headers.authorization;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(normalizeApiError(error));
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = String(originalRequest.url || '');
      
      console.log(`[supplier-auth-debug] event: api-401, url: ${url}, method: ${originalRequest.method}`);

      if (
        url.includes('/supplier/auth/refresh') ||
        url.includes('/auth/supplier/login') ||
        url.includes('/supplier/auth/logout') ||
        url.includes('/auth/supplier/register') ||
        url.includes('/auth/supplier/firebase')
      ) {
        return Promise.reject(normalizeApiError(error));
      }
      if (!shouldAttemptCookieRefresh(error)) {
        console.log(`[supplier-auth-debug] event: logout, reason: shouldAttemptCookieRefresh returned false, url: ${url}, source: api interceptor`);
        notifySupplierSessionExpired('missing_auth_cookie');
        return Promise.reject(normalizeApiError(error));
      }

      originalRequest._retry = true;
      try {
        await runRefresh();
        originalRequest.withCredentials = true;
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization;
          delete originalRequest.headers.authorization;
        }
        return api(originalRequest);
      } catch (refreshError) {
        stripLegacyJwtKeys();
        console.log(`[supplier-auth-debug] event: logout, reason: refresh failed, url: ${url}, source: api interceptor`);
        notifySupplierSessionExpired('refresh_failed');
        return Promise.reject(normalizeApiError(refreshError));
      }
    }

    return Promise.reject(normalizeApiError(error));
  }
);

attachSupplierAxiosPerf(api, 'supplier-api');

export const apiService = {
  get: async (url, params = {}) => {
    const response = await api.get(url, { params });
    return response.data;
  },

  post: async (url, data = {}) => {
    const response = await api.post(url, data);
    return response.data;
  },

  put: async (url, data = {}) => {
    const response = await api.put(url, data);
    return response.data;
  },

  patch: async (url, data = {}) => {
    const response = await api.patch(url, data);
    return response.data;
  },

  delete: async (url) => {
    const response = await api.delete(url);
    return response.data;
  },
};

export default api;
